import { isGeneratingTasksAtom } from '@/atoms/panelAtoms';
import { GET_CLINICAL_TASKS_BY_SESSION_ID } from '@/hooks/query/useQueryClinicalTasksBySessionId';
import { TabType } from '@heidi/session/contracts';
import { type SessionWindow } from '@heidi/session/data';

import { trackTaskEvent } from '@/lib/analytics/scribe/task';
import { generateTasksExcludingCompleted } from '@/lib/api/in-session-guidance';
import { TaskEventType } from '@/models/analytics/segment-names';
import { generateSessionClinicalTasks } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  GET_CLINICAL_TASKS,
  type GetClinicalTasksResponse,
} from '@heidi/tasks/contracts';
import { getDefaultStore } from 'jotai';
import { remove } from 'lodash-es';

/** Exported for tests; keep in sync with polling behavior in schedule fn. */
export const BG_CLINICAL_TASKS_REFETCH_DELAYS_MS = [2500, 8000, 20000, 35000] as const;

/** Cancels any in-flight BG refetch schedule (new session / duplicate completion). */
let activeBgClinicalTasksRefetchCleanup: (() => void) | null = null;

/**
 * BG autogen skips POST /clinical-tasks/session/:id/generate; React Query stays stale
 * until refetch. Schedules delayed `invalidateQueries`; return value clears pending timers
 * (`timerIds.forEach(clearTimeout)`). Also: supersede on repeat schedule, short-circuit
 * when cache already has tasks (before and after each invalidate), `await`
 * invalidation so post-check matches refetched data. Sets isGeneratingTasksAtom
 * for the session (same UX as generateClinicalTasksForSession) while polling.
 */
export const scheduleClinicalTasksRefetchAfterBackgroundAutogen = (
  sessionId: string
): (() => void) => {
  activeBgClinicalTasksRefetchCleanup?.();
  activeBgClinicalTasksRefetchCleanup = null;

  const jotaiStore = getDefaultStore();
  const setGenerating = (value: boolean) => {
    jotaiStore.set(isGeneratingTasksAtom, (prev) => ({
      ...prev,
      [sessionId]: value,
    }));
  };

  setGenerating(true);

  const queryKey = [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }] as const;
  const timerIds: ReturnType<typeof setTimeout>[] = [];
  let cancelled = false;
  let completedPolls = 0;

  const cleanup = (): void => {
    if (cancelled) {
      return;
    }
    cancelled = true;
    timerIds.forEach(clearTimeout);
    setGenerating(false);
    if (activeBgClinicalTasksRefetchCleanup === cleanup) {
      activeBgClinicalTasksRefetchCleanup = null;
    }
    // However the schedule ends, mark the global Tasks lists stale so the next
    // visit refetches.
    void reactQueryClient.invalidateQueries({ queryKey: [GET_CLINICAL_TASKS] });
  };

  const maybeStopIfTasksPresent = () => {
    if (cancelled) {
      return;
    }
    const data = reactQueryClient.getQueryData<GetClinicalTasksResponse>(queryKey);
    if (data?.tasks && data.tasks.length > 0) {
      cleanup();
    }
  };

  for (const delayMs of BG_CLINICAL_TASKS_REFETCH_DELAYS_MS) {
    timerIds.push(
      setTimeout(() => {
        void (async () => {
          if (cancelled) {
            return;
          }
          // Skip redundant refetches if an earlier tick (or another writer) already
          // populated tasks; also cancels remaining timers.
          maybeStopIfTasksPresent();
          if (cancelled) {
            return;
          }
          try {
            await reactQueryClient.invalidateQueries({ queryKey });
          } finally {
            if (!cancelled) {
              maybeStopIfTasksPresent();
            }
          }
          if (cancelled) {
            return;
          }
          completedPolls += 1;
          if (completedPolls === BG_CLINICAL_TASKS_REFETCH_DELAYS_MS.length) {
            cleanup();
          }
        })();
      }, delayMs)
    );
  }

  activeBgClinicalTasksRefetchCleanup = cleanup;
  return cleanup;
};

/** Clears pending BG clinical-task refetch timers (e.g. user navigates away from session). */
export const cancelClinicalTasksBackgroundRefetchSchedule = (): void => {
  activeBgClinicalTasksRefetchCleanup?.();
  activeBgClinicalTasksRefetchCleanup = null;
};

export const closeSplitScreen = (
  sessionWindows: SessionWindow[],
  setSessionWindows: (windows: SessionWindow[]) => void,
  setActiveWindowIndex: (index: number) => void
) => {
  if (sessionWindows?.length === 2) {
    const leftWindow = sessionWindows.at(0);
    const rightWindow = sessionWindows.at(1);
    if (leftWindow && rightWindow) {
      leftWindow.tabs = [...leftWindow.tabs, ...rightWindow.tabs];
      leftWindow.activeTab = {
        name: TabType.NOTE,
      };
      rightWindow.tabs = remove(rightWindow.tabs || [], (t) => {
        return t.name !== TabType.NOTE;
      });
      rightWindow.activeTab = rightWindow.tabs.at(rightWindow.tabs.length - 1);
      setSessionWindows([leftWindow]);
    }
  }
  setActiveWindowIndex(0);
};

export const generateClinicalTasksForSession = async (
  sessionId: string
): Promise<void> => {
  const jotaiStore = getDefaultStore();

  jotaiStore.set(isGeneratingTasksAtom, (prev) => ({
    ...prev,
    [sessionId]: true,
  }));

  try {
    const { data } = await generateSessionClinicalTasks(sessionId);
    reactQueryClient.setQueryData(
      [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }],
      data
    );
    // Generated tasks also belong in the global Tasks page lists. Mark those stale
    // so the next visit refetches.
    void reactQueryClient.invalidateQueries({ queryKey: [GET_CLINICAL_TASKS] });
    // Track each suggested task – defensively & optionally batch
    if (Array.isArray(data?.tasks)) {
      data.tasks.forEach((task) => {
        setTimeout(() => {
          trackTaskEvent({
            taskEventType: TaskEventType.TaskSuggested,
            task,
            sessionId,
          });
        }, 300);
      });
    } else {
      logger.warn('generateClinicalTasksForSession: expected tasks array, got', data);
    }
  } catch (err) {
    logger.error('Failed to generate tasks for session:', sessionId, err);
  } finally {
    jotaiStore.set(isGeneratingTasksAtom, (prev) => ({
      ...prev,
      [sessionId]: false,
    }));
  }
};

/**
 * Nudge-session task generation: calls the in-session-guidance endpoint that excludes
 * protocol actions the clinician already ticked off, then invalidates the clinical-tasks
 * query so the (excluded) list is refetched. Used in place of background autogen for
 * ChenMed nudge sessions, mirroring the generating-atom UX of the other paths.
 */
export const generateClinicalTasksWithExclusionsForSession = async (
  sessionId: string
): Promise<void> => {
  const jotaiStore = getDefaultStore();
  jotaiStore.set(isGeneratingTasksAtom, (prev) => ({
    ...prev,
    [sessionId]: true,
  }));

  try {
    await generateTasksExcludingCompleted(sessionId);
    await reactQueryClient.invalidateQueries({
      queryKey: [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }],
    });
    // Same as generateClinicalTasksForSession: the global lists are stale too.
    void reactQueryClient.invalidateQueries({ queryKey: [GET_CLINICAL_TASKS] });
  } catch (err) {
    logger.error('Failed to generate nudge-aware tasks for session:', sessionId, err);
  } finally {
    jotaiStore.set(isGeneratingTasksAtom, (prev) => ({
      ...prev,
      [sessionId]: false,
    }));
  }
};
